<template>
  <div class="home d-flex flex-column p-4 h-100 position-relative">
    <!-- BACKGROUND DECORATION -->
    <div class="bg-decoration"></div>

    <!-- WELCOME HEADER -->
    <div class="welcome-container mb-5" ref="header">
      <h1 class="text-white font-weight-bold mb-2">Welcome back, {{ firstName }}</h1>
      <p class="text-muted-light lead">What would you like to manage today?</p>
    </div>

    <!-- GRID LAYOUT -->
    <div class="dashboard-grid mb-5" ref="grid">
      
      <!-- CONTENT SECTION -->
      <router-link to="/inbox" class="dash-card card-glass" v-if="canViewContent">
        <div class="card-icon mb-3 text-primary"><font-awesome-icon icon="inbox" size="2x"/></div>
        <h4 class="card-title text-white">Inbox</h4>
        <p class="card-desc text-muted-light">Review pending content</p>
      </router-link>

      <router-link to="/media" class="dash-card card-glass" v-if="canViewContent">
        <div class="card-icon mb-3 text-success"><font-awesome-icon icon="photo-video" size="2x"/></div>
        <h4 class="card-title text-white">Media</h4>
        <p class="card-desc text-muted-light">Manage library assets</p>
      </router-link>

      <router-link to="/categories" class="dash-card card-glass" v-if="canViewContent">
        <div class="card-icon mb-3 text-info"><font-awesome-icon icon="layer-group" size="2x"/></div>
        <h4 class="card-title text-white">Categories</h4>
        <p class="card-desc text-muted-light">Organize content structure</p>
      </router-link>

      <!-- OPERATIONS SECTION -->
      <router-link to="/users" class="dash-card card-glass" v-if="canManageUsers">
        <div class="card-icon mb-3 text-warning"><font-awesome-icon icon="user-group" size="2x"/></div>
        <h4 class="card-title text-white">Users</h4>
        <p class="card-desc text-muted-light">Manage team access</p>
      </router-link>

      <router-link to="/partners" class="dash-card card-glass" v-if="canViewFinance">
        <div class="card-icon mb-3 text-danger"><font-awesome-icon icon="store" size="2x"/></div>
        <h4 class="card-title text-white">Partners</h4>
        <p class="card-desc text-muted-light">External relationships</p>
      </router-link>

      <!-- SYSTEM/FINANCE SECTION -->
      <router-link to="/finance" class="dash-card card-glass" v-if="canViewFinance">
        <div class="card-icon mb-3 text-secondary"><font-awesome-icon icon="chart-line" size="2x"/></div>
        <h4 class="card-title text-white">Finance</h4>
        <p class="card-desc text-muted-light">Revenue and reporting</p>
      </router-link>
      
      <router-link to="/stats" class="dash-card card-glass" v-if="canViewStats">
        <div class="card-icon mb-3 text-light"><font-awesome-icon icon="chart-pie" size="2x"/></div>
        <h4 class="card-title text-white">Statistics</h4>
        <p class="card-desc text-muted-light">Platform analytics</p>
      </router-link>
    </div>

    <!-- INFO PANELS ROW -->
    <div class="info-panels-row" v-if="canViewContent">
      
      <!-- RECENT ACTIVITY -->
      <div class="info-panel" ref="recent" v-if="recentItems.length > 0">
        <h5 class="section-title text-muted-light mb-3">Recent Updates</h5>
        <div class="panel-content glass-content">
          <router-link 
            v-for="item in recentItems" 
            :key="item.id" 
            to="/media"
            class="activity-item"
          >
            <div class="activity-info">
              <span class="activity-title text-white">{{ item.meta && item.meta.title ? item.meta.title : 'Untitled' }}</span>
              <span class="activity-meta text-muted-light">{{ formatDate(item.updated_at) }}</span>
            </div>
            <font-awesome-icon icon="chevron-right" class="activity-arrow text-muted"/>
          </router-link>
        </div>
      </div>

      <!-- INBOX STATUS -->
      <div class="info-panel" ref="inboxStats" v-if="inboxItems.length > 0">
        <h5 class="section-title text-muted-light mb-3">Inbox Status</h5>
        <div class="panel-content glass-content p-3">
          <div class="stats-list">
            <div v-for="(count, status) in inboxStats" :key="status" class="stat-row">
              <span class="stat-label text-capitalize">
                <span :class="getStatusColor(status)" class="status-dot"></span>
                {{ status }}
              </span>
              <span class="stat-count text-white">{{ count }}</span>
            </div>
          </div>
          <div class="inbox-link mt-3 pt-3 border-top border-dark-glass">
             <router-link to="/inbox" class="btn btn-sm btn-block btn-glass text-primary">
               Go to Inbox <font-awesome-icon icon="arrow-right" class="ml-1"/>
             </router-link>
          </div>
        </div>
      </div>

    </div>

    <!-- HIDDEN UTILS -->
    <iframe id="download-all-content-frame" style="display:none;"></iframe>
  </div>
</template>

<script>
import { animate, stagger } from "motion"
import config from "@/appConfig"

export default {
  name: "Home",

  data() {
    return {
      inboxItems: []
    }
  },

  computed: {
    user() {
      return this.$store.state.session.user
    },
    firstName() {
      if (this.user && this.user.name) {
        return this.user.name.split(' ')[0]
      }
      return 'Editor' // Fallback
    },
    userType() {
      return this.user ? this.user.type : null
    },
    config() {
      return config
    },
    
    // PERMISSIONS (Mirrors Menu.vue)
    canViewContent() {
      return ['editor', 'editor_in_chief', 'admin'].includes(this.userType)
    },
    canManageUsers() {
      return ['support', 'admin'].includes(this.userType)
    },
    canViewFinance() {
      return ['finance', 'admin', 'editor', 'editor_in_chief'].includes(this.userType)
    },
    canViewStats() {
       return ['finance', 'admin', 'editor', 'editor_in_chief'].includes(this.userType)
    },

    // COMPUTATIONS
    recentItems() {
      const items = this.$store.state.media.items || []
      return [...items]
        .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))
        .slice(0, 10)
    },
    inboxStats() {
      const stats = {}
      this.inboxItems.forEach(item => {
        // Normalize status if needed, assuming API returns lowercase
        const s = item.status || 'unknown'
        stats[s] = (stats[s] || 0) + 1
      })
      return stats
    }
  },

  mounted() {
    this.$root.app.setActivePageTitle("Dashboard", false)
    this.$root.$emit('set-toolbar', null) // Clear toolbar for dashboard

    this.loadInbox()
    this.animateEntrance()
  },

  methods: {
    loadInbox() {
      if (!this.canViewContent) return
      
      fetch('/admin/api/v2/assets/inbox')
        .then(resp => resp.json())
        .then(resp => {
          this.inboxItems = resp.data || []
        })
        .catch(err => console.error("Failed to load inbox", err))
    },

    formatDate(dateStr) {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },

    getStatusColor(status) {
      const colors = {
        error: 'bg-danger',
        processing: 'bg-warning',
        ready: 'bg-success',
        pending: 'bg-info'
      }
      return colors[status] || 'bg-secondary'
    },

    exportContent() {
      this.$http.postJSON(
        "/admin/api/v2/media/download-all-content-export",
        {},
        (resp) => {
          let file_name = "/admin/api/v2/media/download-export/" + resp.data;
          document.getElementById('download-all-content-frame').src = file_name;
        },
        (status, html) => {
          this.$root.app.showErrorDialog(html, status.toString())
        }
      )
    },

    animateEntrance() {
      // Animate Header
      animate(this.$refs.header, 
        { y: [-20, 0], opacity: [0, 1] }, 
        { duration: 0.8, easing: "ease-out" }
      )

      // Animate Grid Items
      const cards = document.querySelectorAll('.dash-card')
      if (cards.length > 0) {
        animate(cards, 
          { y: [30, 0], opacity: [0, 1] }, 
          { delay: stagger(0.05), duration: 0.5, easing: "ease-out" }
        )
      }

      // Animate Info Panels
      const panels = document.querySelectorAll('.info-panel')
      if (panels.length > 0) {
        animate(panels,
          { y: [20, 0], opacity: [0, 1] },
          { delay: 0.4, duration: 0.6, easing: "ease-out" }
        )
      }
    }
  },

  created() {
    this.$root.$on('home-content-export', this.exportContent)
  },

  beforeDestroy() {
    this.$root.$off('home-content-export', this.exportContent)
  }
};
</script>

<style lang="scss" scoped>
.home {
  max-width: 1400px;
  margin: 0 auto;
}

/* Background gradient orb */
.bg-decoration {
  position: absolute;
  top: -10%;
  right: -10%;
  width: 600px;
  height: 600px;
  background: radial-gradient(circle, rgba(79, 209, 197, 0.15) 0%, rgba(0,0,0,0) 70%);
  border-radius: 50%;
  z-index: 0;
  pointer-events: none;
}

/* Typography */
.text-muted-light {
  color: #94A3B8;
}

.section-title {
  font-size: 0.85rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

/* Grid Layout */
.dashboard-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 24px;
  z-index: 1;
}

/* Layout for Bottom Info Panels */
.info-panels-row {
  display: grid;
  grid-template-columns: 2fr 1fr; /* Recent gets more space than Stats */
  gap: 24px;
}

.info-panel {
  display: flex;
  flex-direction: column;
}

.glass-content {
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  overflow: hidden;
  height: 100%;
}

/* Glass Cards */
.card-glass {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 32px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.05);
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  text-decoration: none;
  
  &:hover {
    background: rgba(255, 255, 255, 0.08);
    transform: translateY(-5px);
    border-color: rgba(255, 255, 255, 0.15);
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.2);

    .card-icon {
      transform: scale(1.1);
    }
    
    .card-title {
      color: #fff;
    }
  }
}

.card-icon {
  transition: transform 0.3s ease;
  margin-bottom: 20px;
  color: rgba(255,255,255,0.8);
}

.card-title {
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 8px;
}

.card-desc {
  font-size: 0.9rem;
  margin-bottom: 0;
  line-height: 1.5;
}

/* Activity List */
.activity-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  text-decoration: none;
  transition: background 0.2s;
  
  &:last-child {
    border-bottom: none;
  }
  
  &:hover {
    background: rgba(255, 255, 255, 0.05);
    
    .activity-title {
      color: #20B4FF !important;
    }
    
    .activity-arrow {
      transform: translateX(3px);
      color: #fff !important;
    }
  }
}

.activity-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  overflow: hidden;
}

.activity-title {
  font-size: 0.9rem;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: color 0.2s;
}

.activity-meta {
  font-size: 0.75rem;
}

.activity-arrow {
  font-size: 0.8rem;
  margin-left: 0.5rem;
  transition: all 0.2s;
}

/* Inbox Stats */
.stat-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.stats-list .stat-row:last-child {
  border-bottom: none;
}

.stat-label {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: #94A3B8;
  font-size: 0.9rem;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
}

.stat-count {
  font-weight: 600;
  font-size: 1rem;
}

.border-dark-glass {
  border-color: rgba(255, 255, 255, 0.05) !important;
}

.btn-glass {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: #fff;
  transition: all 0.2s;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.2);
  }
}

/* Responsive */
@media (max-width: 992px) {
  .info-panels-row {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .dashboard-grid {
    grid-template-columns: 1fr;
  }
}
</style>
